/*
 * Visualiseur InstrumenPoche en Javascript et SVG
 * @Author Yves Biton (yves.biton@sesamath.net)
 * @License: GNU AGPLv3 https://www.gnu.org/licenses/agpl-3.0.html
 */
const kernel = require('../global/global')
const ActionAncetre = require('./actionAncetre')
const mathjaxLoader = require('../mathjaxLoader')

module.exports = ActionEcrireTexte

/**
 * Action d'écriture d'un texte. Le texte doit avoir été créé auparavant.
 * @constructor
 * @extends iep.actionAncetre
 * @param {iepDoc} doc            le document propriétaire
 * @param {string} id             l'id du texte sur lequel écrire
 * @param {string} couleur        la couleur du texte
 * @param {string} taille         la taille du texte
 * @param {string} texte          Le texte à afficher
 * @param {string} marge          la marge éventuelle
 * @param {string} margeGauche    la marge gauche éventuelle
 * @param {string} margeDroite    la marge droite éventuelle
 * @param {string} margeHaut      la marge haute éventuelle
 * @param {string} margeBas       la marge basse éventuelle
 * @param {string} tempo          le tempo
 */
function ActionEcrireTexte (doc, id, couleur, taille, texte, style, couleurFond,
  opaciteFond, couleurCadre, epaisseurCadre, marge, margeGauche, margeDroite, margeHaut, margeBas, tempo) {
  ActionAncetre.call(this, doc, tempo)
  this.txt = this.doc.getElement(id, 'texte')
  this.id = id
  this.couleur = couleur
  // On remplace dans la taille les lettres O mises par les étourdis à la place de 0
  this.taille = (taille == null) ? '20' : taille.replace(/O/g, '0')
  this.texte = kernel.remplaceAccentsHtml(kernel.remplaceBalises(kernel.remplaceCarSpe(texte)))
  this.style = (style === null) ? 'texte' : style
  this.couleurFond = couleurFond
  this.opaciteFond = (opaciteFond == null) ? '0.6' : parseFloat(opaciteFond) / 100
  // Modification version 5.0.2. Causait des problèmes avec explorer
  // this.couleurCadre = (couleurCadre == null) ? couleur : couleurCadre;
  this.couleurCadre = couleurCadre
  if ((epaisseurCadre !== null) && (couleurCadre === null)) this.couleurCadre = couleur
  //
  this.epaisseurCadre = (epaisseurCadre != null) ? epaisseurCadre : ((couleurCadre == null) ? 0 : 1)
  this.marge = (marge == null) ? 0 : parseFloat(marge)
  this.margeGauche = (margeGauche == null) ? 0 : parseFloat(margeGauche)
  this.margeDroite = (margeDroite == null) ? 0 : parseFloat(margeDroite)
  this.margeHaut = (margeHaut == null) ? 0 : parseFloat(margeHaut)
  this.margeBas = (margeBas == null) ? 0 : parseFloat(margeBas)
  this.estLatex = this.estAffichageLatex()
  this.texte = this.traduitCaracteresGrecs(this.texte)
  if (!this.estLatex) {
    this.texte = this.texte.replace(/\*/g, '×')
    if (kernel.necessiteLatex(texte)) {
      this.texte = '$' + this.traiteMaths(this.texte) + '$'
      this.estLatex = true
    } else this.texte = kernel.traiteAccents(this.texte)
  }
}

ActionEcrireTexte.prototype = new ActionAncetre()
/**
 * Action écrivant le contenu du texte sur la figure
 * @param {Boolean} immediat
 * @returns {undefined}
 */
ActionEcrireTexte.prototype.execute = function (immediat) {
  if (this.txt != null) { // Pour éviter les erreurs des auteurs
    this.angle = 0 // Car on peut dans la version js faire toruner un texte
    const txt = this.txt
    txt.couleur = this.couleur
    txt.taille = this.taille
    txt.texte = this.texte
    txt.couleurFond = this.couleurFond
    txt.couleurCadre = this.couleurCadre
    txt.epaisseurCadre = this.epaisseurCadre
    txt.updateg(this.g)
    txt.positionne()
  }
  if (!immediat) this.doc.actionSuivante(immediat)
}

/**
 * Fonction renvoyant true si l'affichage de texte utilise le LaTeX de façon délibérée
 * en encadrant alors le code LaTeX par deux caractères $
 * A priori ne sera utilisé que pour une prochaine version
 * @returns {boolean}
 */
ActionEcrireTexte.prototype.estAffichageLatex = function () {
  return ((this.texte.charAt(0) === '$') && (this.texte.charAt(this.texte.length - 1) === '$'))
}

/**
 * Fonction remplaçant les codes du type £...£ représentant des caractères grecs
 * par les caractères UTF8 correspondants
 * @param {string} ch
 * @returns {string}
 */
ActionEcrireTexte.prototype.traduitCaracteresGrecs = function (ch) {
  let i, search
  const ta = kernel.caracteresGrecs
  if (ch.indexOf('£') !== -1) {
    for (i = 0; i < ta.length; i++) {
      search = '£' + ta[i] + '£'
      if (ch.indexOf(search) !== -1) ch = ch.replace(new RegExp(search, 'g'), kernel.caracteresGrecsUtf8[i])
    }
  }
  return ch
}
/**
 * Fonction traitant la chaîne ch pour la transcrire en code LaTeX
 * Renvoie la chaîne LaTeX correspondante.
 * Si la chaîne contient des balises <br>, un tableau LaTeX est utilisé pour rendre le contenu
 * @param {string} ch : la chaîne à traiter
 * @returns {string} la chaîne LaTeX correpondante une fois ch traduite
 */
ActionEcrireTexte.prototype.traiteMaths = function (ch) {
  let i
  const ch2 = ch
  const bStyletTexte = this.style === 'texte'
  const a = ch2.split(new RegExp('<br>|</br>', 'gi')) // On recherche les codes <br> et on remplace les lignes par une matrice
  if (a.length <= 1) return kernel.traiteAccents(kernel.getMaths(ch, bStyletTexte)) // True pour mode texte par défaut
  let res = '\\begin{array}{l}'
  for (i = 0; i < a.length; i++) {
    if (i !== 0) res += '\\\\'
    res += kernel.traiteAccents(kernel.getMaths(a[i], bStyletTexte)) // true pour mode texte par défaut
  }
  res += '\\end{array}'
  return res
}
/**
 * Fonction appelée lors de l'appel de iepDoc.creeActions()
 * Si l'écriture du texte ne nécessite pas l'emploi du LaTeX, on crée l'élément grapique qui sera
 * démasqué quand l'action sera exécutée
 * Sinon, on charge mathjax si besoin et on crée un div provisoire dans la figure
 * dans lequel on met la formule et on demande à MathJax via sa pile d'appels
 * de traiter ce div pour le remplacer par un svg
 * Via la pile d'appel de MathJax, setReady sera appelée
 * une fois le svg représentant la figure prêt . Cette fonction de callback appelera ensuite
 * iepDoc.waitForReadyState qui est la fonction attendant que tout les objets asynchrones soient prêts
 * (images et affichages de texte utilisant le LaTeX)
 */
ActionEcrireTexte.prototype.prepare = function () {
  if (this.estLatex) {
    this.div = document.createElement('div')
    // On diminue la taille pour le LaTeX eet on ne spécifie pas la taille en px
    // différence par rapport à MathGraph32
    this.div.setAttribute('style', 'top:0px;left:0px;position:absolute;font-family: "Times New Roman", Times, serif;font-size:' +
      String(this.taille - 2) + 'px;visibility:hidden;')
    this.div.setAttribute('id', this.id + 'iepprov')
    const ch = '$$' + this.prelatex() + this.texte.substring(1) + '$'
    this.div.appendChild(document.createTextNode(ch))
    document.body.appendChild(this.div)
    const self = this
    mathjaxLoader().then(() => {
      /* global MathJax */
      // MathJax.Hub.Typeset(this.div);
      MathJax.Hub.Queue(['Typeset', MathJax.Hub, self.div])
      MathJax.Hub.Queue([function () {
        self.creegLatex()
        self.setReady()
      }])
    }).catch(error => {
      console.error(Error('Mathjax n’a pas été chargé correctement, impossible d’afficher du texte en LaTeX'), error)
    })
  } else {
    this.creeg()
    this.setReady()
  }
}

/**
 * Fonction renvoyant l'en-tête à rajouter pour que, dans le cas d'utilisation du
 * LaTeX, l'affichage soit de la bonne couleur.
 */
ActionEcrireTexte.prototype.prelatex = function () {
  return '\\color{' + this.couleur + '}'
}

/**
 * Fonction appelée par prepare() qui récupère l'élement svg graphique représentant
 * la formule dans le div provisoire précédemment créé, détruit ensuite ce div provisoire
 * Rajoute les éléments graphiques correspondant à un cadre si une couleur de fond
 * et un cadre ont été demandés
 */
ActionEcrireTexte.prototype.creegLatex = function () {
  let w, h
  try {
    const g = document.createElementNS(kernel.svgsn, 'g')
    const c1 = this.div.childNodes[1]
    if (!c1) return document.createElementNS(kernel.svgsn, 'g')
    const c2 = c1.childNodes[0]
    if (!c2) return document.createElementNS(kernel.svgsn, 'g')
    let s = c2.childNodes[0]
    if (!s) return document.createElementNS(kernel.svgsn, 'g')
    // Pour gérer Chrome
    while (s.tagName === 'SPAN') s = s.childNodes[0]
    // Le test suivant est du à la compatibilité avec l'explorer
    if ((s.clientWidth !== 0) && (s.clientHeight !== 0)) {
      w = s.clientWidth
      h = s.clientHeight
    } else {
      w = c2.clientWidth
      h = c2.clientHeight
    }
    const t = this.taille
    if (h < t) h = t
    /* Modifié version 5.0.2 pour assurer compatibilité avec explorer
    s.setAttribute("x","0");
    s.setAttribute("y", String(-this.taille)); // Différent de MathGraph32
    s.setAttribute("width", w+"px");
    s.setAttribute("height",h+"px");
    g.appendChild(s.parentNode.removeChild(s));
    */
    const clone = s.cloneNode(true)
    clone.setAttribute('x', '0')
    clone.setAttribute('y', String(-this.taille)) // Différent de MathGraph32
    clone.setAttribute('width', w + 'px')
    clone.setAttribute('height', h + 'px')
    g.appendChild(clone)
    document.body.removeChild(this.div)
    g.setAttribute('visibility', 'hidden')
    // g.setAttribute("id",this.id);
    if ((this.couleurFond !== null) || (this.couleurCadre !== null)) {
      g.setAttribute('visibility', 'hidden')
      this.doc.svg.appendChild(g)
      this.rectAff = {}
      const epc = parseFloat(this.epaisseurCadre)
      this.rectAff.height = g.getBBox().height + 2 * epc + 4 + 2 * this.marge + this.margeHaut + this.margeBas
      this.rectAff.width = g.getBBox().width + 2 * epc + 2 + 2 * this.marge + this.margeGauche + this.margeDroite
      this.rectAff.x = g.getBBox().x - epc - 1 - this.marge - this.margeGauche
      this.rectAff.y = g.getBBox().y - epc - 1 - this.marge - this.margeHaut
      g.setAttribute('visibility', 'visible')
      this.doc.svg.removeChild(g)
      this.creeRectangle(g, (this.couleurFond == null) ? 'white' : this.couleurFond)
    }
    this.g = g
  } catch (e) {
    if (this.div != null) document.body.removeChild(this.div)
    this.g = document.createElementNS(kernel.svgsn, 'g') // Crée un g vide en cas de problème
  }
}

/**
 * Retourne l'élément graphique associé dans le cas où l'affichage n'utilise pas le LaTeX
 * Avant appel, this.text doit voir été affecté
 */
ActionEcrireTexte.prototype.creeg = function () {
  let indbalise, tspan, ch, ch2, style, txt, ind, indexp, indind, mini, bexp, tailleind, stylespan,
    y, sp, i, debutLigne, an
  let dy = 0 // Le baselineshift de chaque tspan
  const inf = '<' // Equivalent du symbole <
  const debexp = '£e('
  const debind = '£i('
  const g = document.createElementNS(kernel.svgsn, 'g')
  const hautlig = parseFloat(this.taille) + 2
  let decblp = 0 // Décalage vers le bas de la ligne précédente
  const decalage = parseFloat(this.taille) * 0.4 // Décalage vers le bas ou le haut en cas d'indice ou d'exposant

  if (this.texte !== '') {
    txt = document.createElementNS(kernel.svgsn, 'text')
    txt.setAttribute('pointer-events', 'none')
    txt.setAttribute('x', 0)
    txt.setAttribute('y', 0)
    style = 'text-anchor:left;font-size:' + this.taille + 'px;' + 'fill:' + this.couleur + ';'
    txt.setAttribute('style', style)
    // On remplace les espaces par des espaces insécables
    ch2 = this.texte.replace(new RegExp(' ', 'g'), '\u00A0')
    // Affecter une longueur ne peut être efficace que pour du texte simpel sans balise
    if ((this.texte.indexOf(inf) === -1) && (this.texte.indexOf(debexp) === -1) && (this.texte.indexOf(debind) === -1) &&
      (this.texte.indexOf('<br>') === -1)) {
      txt.appendChild(document.createTextNode(ch2))
    } else {
      sp = ch2.split(/<br>/gi)
      y = -hautlig
      for (i = 0; i < sp.length; i++) {
        debutLigne = true
        ch = sp[i]
        while (ch !== '') {
          indbalise = ch.indexOf(inf)
          indexp = ch.indexOf(debexp)
          indind = ch.indexOf(debind)
          if (debutLigne) y += hautlig + decblp
          if (indexp !== -1) y += decalage
          if ((indbalise === -1) && (indexp === -1) && (indind === -1)) {
            // txt.appendChild(document.createTextNode(this.texte));
            tspan = document.createElementNS(kernel.svgsn, 'tspan')
            tspan.setAttribute('pointer-events', 'none')
            tspan.setAttribute('dy', dy)
            if (debutLigne) {
              tspan.setAttribute('x', 0)
              tspan.setAttribute('y', y)
              debutLigne = false
            }
            dy = 0 // On est revenu au niveau 0
            tspan.appendChild(document.createTextNode(ch))
            txt.appendChild(tspan)
            break
          } else {
            while ((ch.indexOf(inf) !== -1) || (ch.indexOf(debexp) !== -1) ||
                    (ch.indexOf(debind) !== -1)) {
              indbalise = ch.indexOf(inf)
              indexp = ch.indexOf(debexp)
              indind = ch.indexOf(debind)
              if (indbalise === -1) {
                if (indexp === -1) mini = indind
                else mini = (indind === -1) ? indexp : Math.min(indind, indexp)
              } else {
                if (indexp === -1) mini = (indind === -1) ? indbalise : Math.min(indbalise, indind)
                else {
                  if (indind === -1) mini = Math.min(indbalise, indexp)
                  else mini = Math.min(indbalise, indind, indexp)
                }
              }
              if (mini > 0) { // Il y a du texte avant les balises
                tspan = document.createElementNS(kernel.svgsn, 'tspan')
                tspan.setAttribute('pointer-events', 'none')
                tspan.setAttribute('dy', dy)
                if (debutLigne) {
                  tspan.setAttribute('x', 0)
                  tspan.setAttribute('y', y)
                  debutLigne = false
                }
                dy = 0 // On est revenu au niveau 0
                tspan.appendChild(document.createTextNode(ch.substring(0, mini)))
                txt.appendChild(tspan)
                ch = ch.substring(mini)
              } else {
                if (indbalise === 0) {
                  const infoBalise = new kernel.infoBalise(false, false, false, this.couleur, '', this.taille)
                  ind = kernel.indiceFinBalise(ch)
                  if (ind === -1) kernel.traiteBalise(ch, infoBalise, txt, debutLigne, y)
                  else kernel.traiteBalise(ch.substring(0, ind), infoBalise, txt, debutLigne, y)
                  debutLigne = false
                  if (ind !== -1) ch = ch.substring(ind); else ch = ''
                  dy = 0 // On est revenu au niveau 0
                } else { // mini est égal à 0
                  bexp = mini === indexp
                  an = kernel.analyseExposantOuIndice(ch)
                  tspan = document.createElementNS(kernel.svgsn, 'tspan')
                  tspan.setAttribute('pointer-events', 'none')
                  tspan.setAttribute('dy', dy + 'px')
                  if (debutLigne) {
                    tspan.setAttribute('x', 0)
                    tspan.setAttribute('y', y)
                    debutLigne = false
                  }
                  if (an.erreur) tspan.appendChild(document.createTextNode(ch))
                  else {
                    tspan.appendChild(document.createTextNode(an.operande))
                    txt.appendChild(tspan)
                    tspan = document.createElementNS(kernel.svgsn, 'tspan')
                    tspan.setAttribute('pointer-events', 'none')
                    tspan.appendChild(document.createTextNode(an.exposant))
                    dy = decalage
                    if (bexp) dy = -dy
                    else decblp = dy
                    tspan.setAttribute('dy', dy + 'px')
                    tailleind = parseFloat(this.taille) * 0.6
                    stylespan = 'font-size:' + tailleind + 'px;'
                    tspan.setAttribute('style', stylespan)
                    txt.appendChild(tspan)
                    dy = -dy // Car sinon la suite sera décalée aussi en hauteur
                  }
                  ch = an.texte
                }
              }
            }
          }
        }
      }
    }
    g.appendChild(txt)
  }
  g.setAttribute('visibility', 'hidden')
  // g.setAttribute("id",this.id);
  if ((this.couleurFond != null) || (this.couleurCadre != null)) {
    g.setAttribute('visibility', 'hidden')
    this.doc.svg.appendChild(g)
    this.rectAff = {}
    const epc = parseFloat(this.epaisseurCadre)
    this.rectAff.height = g.getBBox().height + 2 * epc + 3 + 2 * this.marge + this.margeHaut + this.margeBas
    this.rectAff.width = g.getBBox().width + 2 * epc + 2 + 2 * this.marge + this.margeGauche + this.margeDroite
    this.rectAff.x = g.getBBox().x - epc - 1 - this.marge - this.margeGauche
    this.rectAff.y = g.getBBox().y - epc - 1 - this.marge - this.margeHaut
    g.setAttribute('visibility', 'visible')
    this.doc.svg.removeChild(g)
    this.creeRectangle(g, (this.couleurFond == null) ? 'white' : this.couleurFond)
  }
  this.g = g
}

/**
 * Renvoie true si la chaîne ch contient des balises &lt;u\> ou \<i\>
 * @param {string} ch
 * @returns {boolean}
 */
ActionEcrireTexte.prototype.contientBalisesItaliqueOuUnderlineOuFontOuBold = function (ch) {
  return new RegExp(/<(u|b|i|font[^>]+)>/, 'gi').test(ch)
}

/**
 * Fonction appelée dans le cas où l'affihcage de texte a une couleur de fond ou doit être encadrée.
 * Elle rajoute le rectangle au g élément avant l'afficgae correspondant au texte (de faaçon à pouvoir
 * le cas échéant effacer le fond avant l'affichage du texte
 * @param {svg.g} : le svg.g élément qui contient l'affichae de texte (normal ou svg dans le cas du LaTeX
 */
ActionEcrireTexte.prototype.creeRectangle = function (g, coulFond) {
  const r = document.createElementNS(kernel.svgsn, 'rect')
  const style = 'stroke-width:' + this.epaisseurCadre + 'px;' + 'stroke:' + this.couleurCadre + ';' +
    'fill:' + coulFond + ';fill-opacity:' + this.opaciteFond + ';'
  r.setAttribute('style', style)
  r.setAttribute('x', this.rectAff.x)
  r.setAttribute('y', this.rectAff.y)
  r.setAttribute('width', this.rectAff.width)
  r.setAttribute('height', this.rectAff.height)
  r.setAttribute('pointer-events', 'none')
  g.insertBefore(r, g.childNodes[0])
}
